import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import { useComplaintModal } from "../context/ComplaintModalContext";

// Guia-pilar (SEO): página evergreen que captura a busca de cabeça "como saber se uma empresa dá retorno".
// Serve de hub: linka p/ /empresas, /indicadores e o fluxo de relato. O SeoMeta.cs injeta o mesmo conteúdo
// (+ links p/ empresas específicas) no server-render p/ o crawler.

function Sinal({ ok, titulo, texto }) {
  return (
    <li className="flex items-start gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <span className={`flex h-11 w-11 flex-none items-center justify-center rounded-xl text-xl font-extrabold text-white ${ok ? "bg-emerald-600" : "bg-rose-500"}`}>
        {ok ? "✓" : "✕"}
      </span>
      <div>
        <h3 className="font-bold text-gray-900">{titulo}</h3>
        <p className="text-sm text-gray-600 mt-1">{texto}</p>
      </div>
    </li>
  );
}

export default function GuiaRetornoPage() {
  const { openComplaint } = useComplaintModal();
  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section className="bg-brand text-white">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <span className="inline-block rounded-full bg-white/10 border border-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-orange-200">
            Guia eaiRH · candidatos
          </span>
          <h1 className="text-3xl md:text-5xl font-extrabold mt-4 leading-tight">
            Como saber se uma empresa <span className="text-orange-400">dá retorno</span> em processo seletivo
          </h1>
          <p className="mt-4 text-lg text-white/90 max-w-2xl">
            O jeito de pesquisar <strong>antes de se candidatar</strong> — pra você não passar semanas no vácuo esperando uma resposta que talvez nunca chegue.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/empresas" className="rounded-xl bg-orange-600 hover:bg-orange-700 px-6 py-3 font-semibold">Consultar uma empresa</Link>
            <button type="button" onClick={() => openComplaint()} className="rounded-xl bg-white/15 hover:bg-white/25 px-6 py-3 font-semibold">Relatar o meu processo</button>
          </div>
        </div>
      </section>

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-12 space-y-14">
        <p className="text-xl text-text font-medium leading-relaxed">
          Você manda o currículo, faz a entrevista, sai animado — e depois, silêncio. Se isso soa familiar, você não está sozinho:
          a <strong>falta de retorno é a reclamação nº 1</strong> de quem se candidata no Brasil. A boa notícia é que dá pra pesquisar antes e saber com quem você está lidando.
        </p>

        {/* Por que somem */}
        <section>
          <h2 className="text-2xl md:text-3xl font-bold text-text">Por que tantas empresas somem depois da entrevista</h2>
          <p className="text-gray-600 mt-3">
            Na maioria das vezes não é maldade — é processo mal estruturado. Uma vaga recebe centenas de candidaturas e o candidato vira "assunto encerrado" sem nunca ter sido avisado.
            O problema é que, pra quem está do outro lado, o silêncio custa caro: você segura outras decisões e perde a confiança no próximo processo.
          </p>
          <p className="text-gray-600 mt-3">
            Dar retorno é, no fim, um <strong>sinal de cultura</strong>. Empresa que respeita o tempo do candidato na hora de recrutar costuma respeitar o funcionário depois. Por isso vale pesquisar o <em>processo seletivo</em> — não só o emprego.
          </p>
        </section>

        {/* 5 sinais */}
        <section>
          <h2 className="text-2xl md:text-3xl font-bold text-text">Os 5 sinais de que uma empresa dá (ou não) retorno</h2>
          <ul className="mt-6 space-y-3">
            <Sinal ok titulo="A vaga é clara e transparente" texto="Descrição honesta, etapas descritas e, de preferência, faixa salarial. Quem esconde tudo no começo costuma sumir no fim." />
            <Sinal ok titulo="O recrutador combina prazos — e cumpre" texto="&quot;Te retorno até sexta&quot; e realmente retorna. Prazo combinado e cumprido é o melhor indicador de respeito." />
            <Sinal ok titulo="Existe feedback depois de cada etapa" texto="Mesmo o &quot;não&quot; vem com um porquê. Empresa que dá feedback estruturado é a exceção que vale ouro." />
            <Sinal ok={false} titulo="Pede &quot;teste&quot; que é trabalho de verdade" texto="Desafio de 2 horas é normal. Entregar uma campanha, um projeto ou um código que a empresa vai usar, de graça, é sinal de alerta." />
            <Sinal ok={false} titulo="A reputação pública não bate com o discurso" texto="Vários relatos de candidatos dizendo a mesma coisa — vácuo, desorganização, processo interminável — raramente são coincidência." />
          </ul>
          <div className="mt-6 rounded-2xl border border-gray-200 border-l-4 border-l-orange-500 bg-orange-50/60 p-5">
            <p className="text-gray-700">
              <strong>É aqui que o eaiRH entra.</strong> A gente reúne relatos reais de candidatos sobre cada empresa e calcula uma <strong>nota de retorno</strong> — e a empresa tem <strong>direito de resposta</strong>. Você pesquisa antes; ela se explica se quiser.
            </p>
          </div>
        </section>

        {/* Onde pesquisar */}
        <section>
          <h2 className="text-2xl md:text-3xl font-bold text-text">Onde pesquisar antes de se candidatar</h2>
          <div className="mt-6 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            {[
              ["eaiRH", <>A reputação do <strong>processo seletivo</strong>: quem dá retorno, quanto demora e como trata o candidato. É o que falta nas outras plataformas.</>],
              ["Glassdoor", <>Foca na experiência de quem já é <strong>funcionário</strong> (salário, clima). Útil, mas não cobre o recrutamento.</>],
              ["LinkedIn", <>Procure quem já passou pela vaga e mande uma mensagem educada. Relato de primeira mão vale muito.</>],
              ["Busca no Google", <>&quot;[nome da empresa] processo seletivo&quot; costuma revelar relatos que a página institucional esconde.</>],
            ].map(([k, v]) => (
              <div key={k} className="grid grid-cols-1 sm:grid-cols-[150px_1fr] border-t border-gray-100 first:border-t-0">
                <div className="px-5 py-4 font-bold text-gray-900 bg-gray-50 sm:bg-white">{k}</div>
                <div className="px-5 py-4 text-sm text-gray-600">{v}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Já foi ghostado */}
        <section>
          <h2 className="text-2xl md:text-3xl font-bold text-text">Já foi ghostado? O que fazer</h2>
          <div className="mt-5 space-y-3 text-gray-600">
            <p><strong className="text-gray-900">1. Cobre uma vez, com educação.</strong> Uma mensagem curta ao recrutador pedindo um retorno é legítima — e às vezes funciona.</p>
            <p><strong className="text-gray-900">2. Registre o relato.</strong> Contar o que aconteceu (anônimo, se quiser) ajuda o próximo candidato a decidir com informação — e pressiona a empresa a melhorar.</p>
            <p><strong className="text-gray-900">3. Siga em frente.</strong> Silêncio prolongado <em>é</em> uma resposta. Não segure a sua vida esperando quem não respeita o seu tempo.</p>
          </div>
        </section>

        {/* CTA / hub */}
        <section className="rounded-2xl bg-text text-white p-6 md:p-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold">Pesquise antes. E conte o que você viveu.</h2>
          <p className="text-white/80 mt-3 max-w-xl mx-auto">
            Cada relato ajuda o próximo candidato a decidir com informação — e dá à empresa a chance de responder. Grátis pros dois lados.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Link to="/empresas" className="rounded-xl bg-orange-600 hover:bg-orange-700 px-6 py-3 font-semibold">Ver empresas avaliadas</Link>
            <Link to="/indicadores" className="rounded-xl bg-white/15 hover:bg-white/25 px-6 py-3 font-semibold">Ranking de quem dá retorno</Link>
            <button type="button" onClick={() => openComplaint()} className="rounded-xl bg-white/15 hover:bg-white/25 px-6 py-3 font-semibold">Relatar o meu processo</button>
          </div>
        </section>
      </main>
    </div>
  );
}
